import {
  ResizeType,
  type AnimateImageRequest,
  type AnimationFrameOptions,
  type CropImageRequest,
  type DeskewImageRequest,
  type EditImageRequest,
  type FilterEffectImageRequest,
  type FlipRotateImageRequest,
  type ResizeImageRequest,
  type WatermarkImageRequest,
} from '@imagise/generated-imagise-cloud-api-client';
import type { WireEditStep, EditWireRequest } from '@imagise/entities';
import { FilterTypes } from './enums/editor.enum';
import { normalizeWatermarkFontName } from './models/watermark-fonts';
import {
  EditorCommittedEdit,
  EditorFilterCommittedEdit,
  EditorFormValue,
  applyEditToSize,
} from './editor-domain.models';

export type { EditImageRequest };

type AdapterMappingStatus = 'supported' | 'unsupported';
type FilterEffectRequestType = 'FilterEffectImageRequest';
type BackendFilterEffectName = keyof typeof BACKEND_FILTER_EFFECT;

interface SupportedEditMapping<TRequest extends WireEditStep | null> {
  status: 'supported';
  request: TRequest;
}

interface UnsupportedEditMapping {
  status: 'unsupported';
  reason: string;
}

type EditMappingResult =
  | SupportedEditMapping<WireEditStep | null>
  | UnsupportedEditMapping;

const ROTATE_FLIP_TYPE_BY_KEY: Readonly<Record<string, number>> = {
  '0:false:false': 0,
  '90:false:false': 1,
  '180:false:false': 2,
  '270:false:false': 3,
  '0:true:false': 4,
  '90:true:false': 5,
  '180:true:false': 6,
  '270:true:false': 7,
  '0:false:true': 8,
  '90:false:true': 9,
  '180:false:true': 10,
  '270:false:true': 11,
  '0:true:true': 12,
  '90:true:true': 13,
  '180:true:true': 14,
  '270:true:true': 15,
};

const BACKEND_FILTER_EFFECT = {
  BigRectangular: 0,
  SmallRectangular: 1,
  Median: 2,
  GaussWiener: 3,
  MotionWiener: 4,
  GaussianBlur: 5,
  Sharpen: 6,
  BilateralSmoothing: 7,
} as const;

const ANIMATION_OUTPUT_FORMATS = new Set(['gif', 'webp', 'apng', 'png']);
const DEFAULT_ANIMATION_OUTPUT_FORMAT = 'gif';

const DOTNET_EDIT_REQUEST_TYPE = {
  crop: 'Imagise.Cloud.Api.DTO.CropImageRequest, Imagise.Cloud.Api',
  resize: 'Imagise.Cloud.Api.DTO.ResizeImageRequest, Imagise.Cloud.Api',
  rotate: 'Imagise.Cloud.Api.DTO.FlipRotateImageRequest, Imagise.Cloud.Api',
  filter: 'Imagise.Cloud.Api.DTO.FilterEffectImageRequest, Imagise.Cloud.Api',
  watermark: 'Imagise.Cloud.Api.DTO.WatermarkImageRequest, Imagise.Cloud.Api',
  deskew: 'Imagise.Cloud.Api.DTO.DeskewImageRequest, Imagise.Cloud.Api',
  animation: 'Imagise.Cloud.Api.DTO.AnimateImageRequest, Imagise.Cloud.Api',
} as const;

export const EDITOR_REQUEST_ADAPTER_CONFIG = {
  resizeType: ResizeType.HighQualityResample,
} as const;

export interface UnsupportedEditorEdit {
  edit: EditorCommittedEdit;
  status: 'unsupported';
  reason: string;
}

export interface EditorFilterCompatibilityEntry {
  filterType: FilterTypes;
  status: AdapterMappingStatus;
  requestType: FilterEffectRequestType;
  backendEffect: number | null;
  backendEffectName: BackendFilterEffectName | null;
  reason: string | null;
}

// Keep the UI -> backend filter support list explicit to avoid accidental approximations.
export const EDITOR_FILTER_COMPATIBILITY_MATRIX: readonly EditorFilterCompatibilityEntry[] = [
  {
    filterType: FilterTypes.None,
    status: 'supported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: null,
    backendEffectName: null,
    reason: 'No-op filter. No backend edit is emitted.',
  },
  {
    filterType: FilterTypes.Exposure,
    status: 'unsupported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: null,
    backendEffectName: null,
    reason: 'UI exposure combines brightness, contrast and gamma, but backend has no matching filter effect.',
  },
  {
    filterType: FilterTypes.Grayscale,
    status: 'unsupported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: null,
    backendEffectName: null,
    reason: 'Backend has no grayscale filter effect.',
  },
  {
    filterType: FilterTypes.Inverse,
    status: 'unsupported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: null,
    backendEffectName: null,
    reason: 'Backend has no invert filter effect.',
  },
  {
    filterType: FilterTypes.Binarize,
    status: 'unsupported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: null,
    backendEffectName: null,
    reason: 'UI binarize uses threshold and low/high colors, but backend has no equivalent filter effect.',
  },
  {
    filterType: FilterTypes.Sharpen,
    status: 'supported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: BACKEND_FILTER_EFFECT.Sharpen,
    backendEffectName: 'Sharpen',
    reason: null,
  },
  {
    filterType: FilterTypes.Outline,
    status: 'unsupported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: null,
    backendEffectName: null,
    reason: 'Backend has no outline filter effect.',
  },
  {
    filterType: FilterTypes.MotionBlur,
    status: 'unsupported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: BACKEND_FILTER_EFFECT.MotionWiener,
    backendEffectName: 'MotionWiener',
    reason: 'UI motion blur convolution does not match backend MotionWiener and also lacks Size, Sigma and Angle.',
  },
  {
    filterType: FilterTypes.Emboss,
    status: 'unsupported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: null,
    backendEffectName: null,
    reason: 'Backend has no emboss filter effect.',
  },
  {
    filterType: FilterTypes.Sobel,
    status: 'unsupported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: null,
    backendEffectName: null,
    reason: 'UI sobel uses directional kernels, but backend has no sobel filter effect.',
  },
  {
    filterType: FilterTypes.GaussianBlur,
    status: 'supported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: BACKEND_FILTER_EFFECT.GaussianBlur,
    backendEffectName: 'GaussianBlur',
    reason: null,
  },
  {
    filterType: FilterTypes.ColorBurn,
    status: 'unsupported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: null,
    backendEffectName: null,
    reason: 'Backend has no color-burn filter effect.',
  },
  {
    filterType: FilterTypes.Cartoon,
    status: 'unsupported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: null,
    backendEffectName: null,
    reason: 'Backend has no cartoon filter effect.',
  },
  {
    filterType: FilterTypes.BigRectangular,
    status: 'supported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: BACKEND_FILTER_EFFECT.BigRectangular,
    backendEffectName: 'BigRectangular',
    reason: null,
  },
  {
    filterType: FilterTypes.SmallRectangular,
    status: 'supported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: BACKEND_FILTER_EFFECT.SmallRectangular,
    backendEffectName: 'SmallRectangular',
    reason: null,
  },
  {
    filterType: FilterTypes.Median,
    status: 'supported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: BACKEND_FILTER_EFFECT.Median,
    backendEffectName: 'Median',
    reason: null,
  },
  {
    filterType: FilterTypes.GaussWiener,
    status: 'supported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: BACKEND_FILTER_EFFECT.GaussWiener,
    backendEffectName: 'GaussWiener',
    reason: null,
  },
  {
    filterType: FilterTypes.MotionWiener,
    status: 'supported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: BACKEND_FILTER_EFFECT.MotionWiener,
    backendEffectName: 'MotionWiener',
    reason: null,
  },
  {
    filterType: FilterTypes.BilateralSmoothing,
    status: 'supported',
    requestType: 'FilterEffectImageRequest',
    backendEffect: BACKEND_FILTER_EFFECT.BilateralSmoothing,
    backendEffectName: 'BilateralSmoothing',
    reason: null,
  },
] as const;

const FILTER_COMPATIBILITY_BY_TYPE = new Map(
  EDITOR_FILTER_COMPATIBILITY_MATRIX.map(entry => [entry.filterType, entry]),
);

export interface BuildEditImageRequestResult {
  request: EditWireRequest;
  unsupportedEdits: UnsupportedEditorEdit[];
}

export interface BuildEditImageRequestOptions {
  additionalEdits?: readonly EditorCommittedEdit[];
  toFormat?: string | null;
}

export function buildEditImageRequestFromState(
  value: EditorFormValue,
  options: BuildEditImageRequestOptions = {},
): BuildEditImageRequestResult {
  if (!value.source.sourceBase64) {
    throw new Error('Editor source base64 is required to build EditImageRequest.');
  }

  const appliedEdits = [
    ...value.committedEdits.slice(0, value.history.cursor),
    ...(options.additionalEdits ?? []),
  ];
  const edits: WireEditStep[] = [];
  const unsupportedEdits: UnsupportedEditorEdit[] = [];
  let currentSize = value.source.width != null && value.source.height != null
    ? {
        width: value.source.width,
        height: value.source.height,
      }
    : null;

  for (const edit of appliedEdits) {
    const mappedEdit = mapEdit(
      edit,
      currentSize,
      options.toFormat ?? value.output.toFormat,
    );

    if (mappedEdit.status === 'supported') {
      if (mappedEdit.request) {
        edits.push(mappedEdit.request);
      }

      if (currentSize) {
        currentSize = applyEditToSize(currentSize, edit);
      }

      continue;
    }

    unsupportedEdits.push({
      edit,
      status: 'unsupported',
      reason: mappedEdit.reason,
    });
  }

  const requestedToFormat = options.toFormat ?? value.output.toFormat;
  const effectiveToFormat = edits.some(isAnimateImageRequest)
    ? normalizeAnimationOutputFormat(requestedToFormat)
    : requestedToFormat;

  return {
    request: {
      toFormat: effectiveToFormat,
      imageBase64Files: [value.source.sourceBase64],
      edits,
    },
    unsupportedEdits,
  };
}

function isAnimateImageRequest(request: WireEditStep): request is WireEditStep<AnimateImageRequest> {
  return request.$type === DOTNET_EDIT_REQUEST_TYPE.animation;
}

function mapEdit(
  edit: EditorCommittedEdit,
  currentSize: { width: number; height: number } | null,
  outputFormat: string | null | undefined,
): EditMappingResult {
  switch (edit.kind) {
    case 'crop':
      return mapCropEdit(edit);
    case 'resize':
      return mapResizeEdit(edit, currentSize);
    case 'rotate':
      return mapRotateEdit(edit);
    case 'filter':
      return mapFilterEdit(edit);
    case 'watermark':
      return mapWatermarkEdit(edit);
    case 'deskew':
      return mapDeskewEdit(edit);
    case 'animation':
      return mapAnimationEdit(edit, outputFormat);
  }
}

function mapCropEdit(
  edit: EditorCommittedEdit,
): EditMappingResult {
  if (edit.kind !== 'crop') {
    return unsupported('Crop mapper received a non-crop edit.');
  }

  const normalizedRect = edit.params.rect;
  const left = Math.round(normalizedRect.x);
  const top = Math.round(normalizedRect.y);
  const right = Math.round(normalizedRect.x + normalizedRect.width);
  const bottom = Math.round(normalizedRect.y + normalizedRect.height);

  return supported({
    $type: DOTNET_EDIT_REQUEST_TYPE.crop,
    cropAreaOptions: [
      {
        pageIndex: 0,
        cornerRadius: 0,
        cropArea: [
          { x: left, y: top },
          { x: right, y: top },
          { x: right, y: bottom },
          { x: left, y: bottom },
        ],
      },
    ],
  } satisfies WireEditStep<CropImageRequest>);
}

function mapResizeEdit(
  edit: EditorCommittedEdit,
  currentSize: { width: number; height: number } | null,
): EditMappingResult {
  if (edit.kind !== 'resize') {
    return unsupported('Resize mapper received a non-resize edit.');
  }

  if (!currentSize) {
    return unsupported('Resize mapping requires known source dimensions.');
  }

  const width = Math.max(1, Math.round(currentSize.width * edit.params.scaleX));
  const height = Math.max(1, Math.round(currentSize.height * edit.params.scaleY));
  const isUniformScale = isNearlyEqual(edit.params.scaleX, edit.params.scaleY);

  if (edit.params.mode === 'percentage' && isUniformScale) {
    return supported({
      $type: DOTNET_EDIT_REQUEST_TYPE.resize,
      sizePercentage: edit.params.scaleX * 100,
      resizeType: EDITOR_REQUEST_ADAPTER_CONFIG.resizeType,
    } satisfies WireEditStep<ResizeImageRequest>);
  }

  return supported({
    $type: DOTNET_EDIT_REQUEST_TYPE.resize,
    width,
    height,
    keepAspectRatio: edit.params.keepAspectRatio,
    resizeType: EDITOR_REQUEST_ADAPTER_CONFIG.resizeType,
  } satisfies WireEditStep<ResizeImageRequest>);
}

function mapRotateEdit(
  edit: EditorCommittedEdit,
): EditMappingResult {
  if (edit.kind !== 'rotate') {
    return unsupported('Rotate mapper received a non-rotate edit.');
  }

  const normalizedRightAngle = normalizeRightAngle(edit.params.rightAngle);
  const rotateFlipType = ROTATE_FLIP_TYPE_BY_KEY[
    `${normalizedRightAngle}:${edit.params.flipX}:${edit.params.flipY}`
  ];

  if (rotateFlipType === undefined) {
    return unsupported('Rotate/flip combination is not supported by backend RotateFlipType.');
  }

  return supported({
    $type: DOTNET_EDIT_REQUEST_TYPE.rotate,
    angle: edit.params.preciseAngle,
    rotateFlipType: rotateFlipType,
    resizeProportionally: !edit.params.cut,
  } satisfies WireEditStep<FlipRotateImageRequest>);
}

function mapFilterEdit(
  edit: EditorCommittedEdit,
): EditMappingResult {
  if (edit.kind !== 'filter') {
    return unsupported('Filter mapper received a non-filter edit.');
  }

  const compatibility = getFilterCompatibility(edit);

  if (compatibility.status === 'unsupported') {
    return unsupported(compatibility.reason ?? 'Filter is not supported by backend adapter.');
  }

  switch (edit.params.filterType) {
    case FilterTypes.None:
      return supported(null);
    default:
      return mapBackendFilterEdit(edit, compatibility);
  }
}

function mapWatermarkEdit(
  edit: EditorCommittedEdit,
): EditMappingResult {
  if (edit.kind !== 'watermark') {
    return unsupported('Watermark mapper received a non-watermark edit.');
  }

  const text = edit.params.text.trim();
  if (!text) {
    return unsupported('Watermark text is required.');
  }

  const fontName = normalizeWatermarkFontName(edit.params.fontName);

  return supported({
    $type: DOTNET_EDIT_REQUEST_TYPE.watermark,
    watermarkText: text,
    textColor: edit.params.textColor || null,
    fontName,
    fontSize: edit.params.fontSize > 0 ? Math.round(edit.params.fontSize) : null,
    rotationAngleDegrees: Math.round(edit.params.rotationAngleDegrees),
  } satisfies WireEditStep<WatermarkImageRequest>);
}

function mapDeskewEdit(
  edit: EditorCommittedEdit,
): EditMappingResult {
  if (edit.kind !== 'deskew') {
    return unsupported('Deskew mapper received a non-deskew edit.');
  }

  return supported({
    $type: DOTNET_EDIT_REQUEST_TYPE.deskew,
    deskewImagePageOptions: [
      {
        pageIndex: 0,
        resizeProportionally: edit.params.resizeProportionally,
        backgroundColor: edit.params.backgroundColor,
      },
    ],
  } satisfies WireEditStep<DeskewImageRequest>);
}

function mapAnimationEdit(
  edit: EditorCommittedEdit,
  outputFormat: string | null | undefined,
): EditMappingResult {
  if (edit.kind !== 'animation') {
    return unsupported('Animation mapper received a non-animation edit.');
  }

  const frames = edit.params.frames
    .filter(frame => Boolean(frame.sourceBase64))
    .map(frame => ({
      ...frame,
      durationMs: normalizeFrameDuration(frame.durationMs),
    }));

  if (frames.length < 2) {
    return unsupported('Animation requires at least two frames.');
  }

  const animationOutputFormat = resolveAnimationOutputFormat(outputFormat);
  if (!animationOutputFormat) {
    return unsupported('Animation output format must be GIF, WEBP, APNG or PNG.');
  }

  // The backend applies FrameOptions positionally to [main image, ...ImageBase64Files].
  const frameOptions: AnimationFrameOptions[] = frames.map((frame, index) => ({
    index,
    time: frame.durationMs,
  }));

  return supported({
    $type: DOTNET_EDIT_REQUEST_TYPE.animation,
    toFormat: animationOutputFormat,
    imageBase64Files: frames.slice(1).map(frame => frame.sourceBase64),
    width: Math.max(1, Math.round(edit.params.width)),
    height: Math.max(1, Math.round(edit.params.height)),
    backgroundColor: edit.params.backgroundColor ?? '',
    loopCount: normalizeLoopCount(edit.params.loopCount),
    frameOptions,
  } satisfies WireEditStep<AnimateImageRequest>);
}

export function getFilterCompatibility(
  edit: Pick<EditorFilterCommittedEdit, 'params'> | FilterTypes,
): EditorFilterCompatibilityEntry {
  const filterType = typeof edit === 'number' ? edit : edit.params.filterType;
  const compatibility = FILTER_COMPATIBILITY_BY_TYPE.get(filterType);

  if (!compatibility) {
    return {
      filterType,
      status: 'unsupported',
      requestType: 'FilterEffectImageRequest',
      backendEffect: null,
      backendEffectName: null,
      reason: `Unknown filter type: ${filterType}.`,
    };
  }

  return compatibility;
}

function normalizeRightAngle(angle: number): number {
  return ((angle % 360) + 360) % 360;
}

function normalizeAnimationOutputFormat(format: string | null | undefined): string {
  return resolveAnimationOutputFormat(format) ?? DEFAULT_ANIMATION_OUTPUT_FORMAT;
}

function resolveAnimationOutputFormat(format: string | null | undefined): string | null {
  const normalized = (format || '').replace(/\s+/g, '').toLowerCase();

  if (!normalized) {
    return DEFAULT_ANIMATION_OUTPUT_FORMAT;
  }

  if (!ANIMATION_OUTPUT_FORMATS.has(normalized)) {
    return null;
  }

  // Plain "png" makes the backend export every frame as a separate page (zip),
  // so request an animated PNG instead.
  return normalized === 'png' ? 'apng' : normalized;
}

function normalizeLoopCount(value: number): number {
  return Math.min(65535, Math.max(0, Math.round(value || 0)));
}

function normalizeFrameDuration(value: number): number {
  return Math.min(32767, Math.max(1, Math.round(value || 1)));
}

function isNearlyEqual(left: number, right: number): boolean {
  return Math.abs(left - right) < 0.0001;
}

function supported<TRequest extends WireEditStep | null>(
  request: TRequest,
): SupportedEditMapping<TRequest> {
  return {
    status: 'supported',
    request,
  };
}

function unsupported(reason: string): UnsupportedEditMapping {
  return {
    status: 'unsupported',
    reason,
  };
}

function mapBackendFilterEdit(
  edit: EditorFilterCommittedEdit,
  compatibility: EditorFilterCompatibilityEntry,
): EditMappingResult {
  const backendEffect = compatibility.backendEffect;
  if (backendEffect == null) {
    return unsupported(
      `Filter ${FilterTypes[edit.params.filterType]} does not have a backend effect mapping.`,
    );
  }

  const args =
    edit.params.filterArgs?.kind === 'backend-filter'
      ? edit.params.filterArgs
      : null;

  switch (edit.params.filterType) {
    case FilterTypes.BigRectangular:
    case FilterTypes.SmallRectangular:
      return supported({
        $type: DOTNET_EDIT_REQUEST_TYPE.filter,
        effect: backendEffect,
      } satisfies WireEditStep<FilterEffectImageRequest>);
    case FilterTypes.Median:
    case FilterTypes.BilateralSmoothing: {
      const size = normalizeRequiredIntegerArg(args?.size);
      if (size == null) {
        return unsupported(
          `Filter ${FilterTypes[edit.params.filterType]} requires a positive Size value.`,
        );
      }

      return supported({
        $type: DOTNET_EDIT_REQUEST_TYPE.filter,
        effect: backendEffect,
        size,
      } satisfies WireEditStep<FilterEffectImageRequest>);
    }
    case FilterTypes.GaussianBlur:
    case FilterTypes.GaussWiener:
    case FilterTypes.Sharpen: {
      const size = normalizeRequiredIntegerArg(args?.size);
      const sigma = normalizeRequiredNumberArg(args?.sigma);
      if (size == null || sigma == null) {
        return unsupported(
          `Filter ${FilterTypes[edit.params.filterType]} requires both Size and Sigma values.`,
        );
      }

      return supported({
        $type: DOTNET_EDIT_REQUEST_TYPE.filter,
        effect: backendEffect,
        size,
        sigma,
      } satisfies WireEditStep<FilterEffectImageRequest>);
    }
    case FilterTypes.MotionWiener: {
      const size = normalizeRequiredIntegerArg(args?.size);
      const sigma = normalizeRequiredNumberArg(args?.sigma);
      const angle = normalizeRequiredNumberArg(args?.angle);
      if (size == null || sigma == null || angle == null) {
        return unsupported('Filter MotionWiener requires Size, Sigma and Angle values.');
      }

      return supported({
        $type: DOTNET_EDIT_REQUEST_TYPE.filter,
        effect: backendEffect,
        size,
        sigma,
        angle,
      } satisfies WireEditStep<FilterEffectImageRequest>);
    }
    default:
      return unsupported(
        `Filter ${FilterTypes[edit.params.filterType]} is marked as supported but its adapter mapping is not implemented.`,
      );
  }
}

function normalizeRequiredIntegerArg(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }

  return Math.max(1, Math.round(value));
}

function normalizeRequiredNumberArg(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null;
  }

  return value;
}
